import { useState } from 'react'
import { Select } from '../ui/Select'
import { Button } from '../ui/Button'
import { cn } from '@/lib/utils'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { AddStoreDialog } from './AddStoreDialog'

type StoreSwitcherProps = {
  className?: string
  /** Sidebar / mobile bar: stack controls so nothing bleeds into the main pane. */
  layout?: 'inline' | 'stacked'
}

export function StoreSwitcher({ className = '', layout = 'inline' }: StoreSwitcherProps) {
  const { stores, currentStoreId, setCurrentStoreId, loading, currentStore } = useCurrentStore()
  const [addOpen, setAddOpen] = useState(false)
  const canAddStore = stores.some((s) => s.role === 'store_owner')
  const stacked = layout === 'stacked'

  if (loading) {
    return (
      <span
        className={cn(
          'inline-block h-9 animate-pulse rounded-xl bg-muted',
          stacked ? 'w-full' : 'min-w-[7rem]',
          className,
        )}
        aria-hidden
      />
    )
  }

  if (stores.length === 0) return null

  const label = currentStore?.name ?? stores[0]?.name ?? 'Store'
  const containerClass = cn(
    'flex min-w-0',
    stacked ? 'w-full flex-col items-stretch gap-2' : 'items-center gap-2',
    className,
  )
  const addButtonClass = stacked ? 'w-full justify-center' : undefined

  if (stores.length <= 1) {
    return (
      <div className={containerClass}>
        <span
          className={cn(
            'inline-flex min-w-0 items-center truncate rounded-xl border border-border bg-card/80 px-3 py-2 text-sm font-medium text-foreground shadow-sm',
            stacked ? 'w-full' : 'max-w-full flex-1 sm:max-w-[14rem] sm:flex-none',
          )}
          title={label}
        >
          {label}
        </span>
        {canAddStore ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={addButtonClass}
              onClick={() => setAddOpen(true)}
            >
              Add store
            </Button>
            <AddStoreDialog open={addOpen} onOpenChange={setAddOpen} />
          </>
        ) : null}
      </div>
    )
  }

  return (
    <div className={containerClass}>
      <Select
        aria-label="Switch store"
        className={cn('min-w-0', stacked ? 'w-full' : 'flex-1 sm:max-w-[14rem] sm:flex-none')}
        value={currentStoreId != null ? String(currentStoreId) : ''}
        onChange={(e) => {
          const next = Number(e.target.value)
          if (Number.isFinite(next) && next > 0) setCurrentStoreId(next)
        }}
      >
        {stores.map((s) => (
          <option key={s.storeId} value={s.storeId}>
            {s.name}
            {s.billingRequired ? ' · billing' : ''}
          </option>
        ))}
      </Select>
      {canAddStore ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={addButtonClass}
          onClick={() => setAddOpen(true)}
        >
          Add store
        </Button>
      ) : null}
      <AddStoreDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  )
}
